import { useCallback, useState } from "react";
import { useFormikContext } from "formik";
import { ZodSchema } from "zod";
import { TaskCreate } from "@common/contracts/task.contract";
import { omitBy } from "@common/helpers/objects";
import { FiltersTypes } from "@common/types/filters/general";
import { PersistableView, View } from "@common/types/views/views";
import { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { keepTaskSelectionOptions } from "./taskCreationPreview";

interface UseTaskCreationPreviewViewParams<TView extends View> {
    initialView: TView;
    viewSchema: ZodSchema<TView>;
}

export function useTaskCreationPreviewView<TView extends View>({
    initialView,
    viewSchema,
}: UseTaskCreationPreviewViewParams<TView>) {
    const { values, setFieldValue } = useFormikContext<TaskCreate>();
    const taskView = values.view;
    const [view, setView] = useState<TView>(initialView);

    const syncTaskView = useCallback(
        (
            next: Partial<
                Pick<PersistableView, "columns" | "options" | "filters">
            >,
        ) => {
            if (!taskView) {
                return;
            }
            void setFieldValue("view", { ...taskView, ...next });
        },
        [setFieldValue, taskView],
    );

    const commitFilters = useCallback(
        (filters: FiltersTypes) => {
            setView((previous) => ({ ...previous, filters }));
            syncTaskView({ filters });
        },
        [syncTaskView],
    );

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (payload) =>
            commitFilters(
                omitBy(
                    { ...view.filters, ...payload },
                    (value) => value === null || value === undefined,
                ),
            ),
        [commitFilters, view.filters],
    );

    // The table edits columns and density; the task keeps its own selection
    // flags, which the preview switches off.
    const handleViewChange = useCallback(
        (updated: View) => {
            const parsed = viewSchema.safeParse({
                ...view,
                columns: updated.columns,
                options: updated.options ?? view.options,
            });
            if (!parsed.success) {
                return;
            }
            setView(parsed.data);
            syncTaskView({
                columns: parsed.data.columns,
                options: keepTaskSelectionOptions({
                    previewOptions: parsed.data.options,
                    taskOptions: taskView?.options,
                }),
            });
        },
        [syncTaskView, taskView?.options, view, viewSchema],
    );

    return { view, commitFilters, handleFiltersChange, handleViewChange };
}
